import React, { ChangeEvent, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TabNavigation, { TabConfig } from '../../Components/common/TabNavigation';
import SearchIcon from '../../Images/search-icon.svg';
import DownloadIcon from '../../Images/download-icon.svg';
import ArrowUp from '../../Images/chevron-up.svg';
import ArrowDown from '../../Images/chevron-down.svg';
import ArrowDefault from '../../Images/Chevron-default-icon.svg';
import Loader from '../../Components/common/loader/loader';
import './Profiles.css';
import '../Summary/Summary.css';


import { getUsers, userDownload, getUserType, getAccountsLite, CustomerAccountLite } from '../../Services/serviceURL';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { CustomerLite } from '../Plants/CreatePlant';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

interface ProfileData {
    id: string;
    customer: string;

    name: string;
    type: string;
    role: string
    email: string;
    status: string;

}

type SortableKeys = keyof ProfileData;

const Profile: React.FC = () => {
    const [sortConfig, setSortConfig] = useState<{ key: SortableKeys; direction: 'asc' | 'desc' } | null>(null);
    const [customers, setCustomers] = useState<CustomerLite[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [isLoadingProfile, setIsLoadingProfile] = useState(true);
    const [allProfiles, setAllProfiles] = useState<ProfileData[]>([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
    const [selectedUserType, setSelectedUserType] = useState<string>('All'); // New state for user type
    const location = useLocation();
    const navigate = useNavigate();
    const userRole = useSelector((state: RootState) => state.auth.userRole);
    const [btnDisabled, setBtnDisabled] = useState(false);
    // Added searchText state

    // Tab configuration
    const tabConfig: TabConfig[] = [
        { key: 'Plants', label: 'Plants', route: '/plants' },
        { key: 'Profile', label: 'Users', route: '/users' },
        { key: 'Securities', label: 'Securities', route: '/securities' }
    ];
    const [searchText, setSearchText] = useState('');

    // Searchable dropdown UI state
    const [isCustomerOpen, setIsCustomerOpen] = useState(false);
    const [customerSearch, setCustomerSearch] = useState('');
    const [isUserTypeOpen, setIsUserTypeOpen] = useState(false);
    const [userTypeSearch, setUserTypeSearch] = useState('');
    const safeValue = (value: any): string => {
        return typeof value === 'string' && value.trim() !== '' ? value : '';
    };

    // Update the search handler
    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
        setCurrentPage(1); // Reset to first page when searching
    };

    useEffect(() => {
        setIsLoadingProfile(true);
        // Initial load with "All" userType and no customerId
        getUsers("All")
            .then((res) => {
                const fetchedData = res.Data;
                if (Array.isArray(fetchedData)) {
                    const mappedData: ProfileData[] = fetchedData.map((usr: any) => ({
                        id: usr.id || Math.random().toString(36).substr(2, 9),
                        customer: safeValue(usr.customer),
                        name: safeValue(usr.name),
                        type: safeValue(usr.type),
                        role: safeValue(usr.role),
                        email: safeValue(usr.email),
                        status: safeValue(usr.status),
                    }));

                    setAllProfiles(mappedData);
                } else {
                    setAllProfiles([]);
                }
            })
            .catch((err) => {
                setAllProfiles([]);
            })
            .finally(() => {
                setIsLoadingProfile(false);
            });
    }, []);

    //scroll
    useEffect(() => {
    const fetchCustomersFromUsers = async () => {
        try {
            // Fetch users with "All" filter to get all customers
            const response = await getUsers("All");
            
            if (response && response.Data && Array.isArray(response.Data)) {
                // Create a Map to store unique customers by a consistent key
                const customerMap = new Map();
                
                response.Data.forEach((user: any, index: number) => {
                    // Get customer name - check multiple possible property names
                    const customerName = user.customer || 
                                       user.customerName || 
                                       user.Customer || 
                                       user.CustomerName || 
                                       '';
                    
                    // Get customer ID - check multiple possible property names
                    const customerId = user.customerId || 
                                      user.CustomerId || 
                                      user.id || 
                                      `temp-${index}`;
                    
                    // Get soldTo - check multiple possible property names
                    const soldTo = user.soldTo || 
                                  user.SoldTo || 
                                  user.salesDistrict || 
                                  user.SalesDistrict || 
                                  '';
                    
                    // Only process if we have a valid customer name
                    if (customerName && customerName.trim() !== '') {
                        // Create a unique key based on customer name (case-insensitive)
                        const customerKey = customerName.trim().toLowerCase();
                        
                        // If we don't have this customer yet, or if this entry has more complete data
                        if (!customerMap.has(customerKey) || 
                            (customerId && !customerId.startsWith('temp-')) ||
                            (soldTo && soldTo.trim() !== '')) {
                            
                            customerMap.set(customerKey, {
                                id: customerId,
                                customerId: customerId,
                                customerName: customerName.trim(),
                                soldTo: soldTo.trim()
                            });
                        }
                    }
                });
                
                // Convert map to array and sort alphabetically
                const uniqueCustomers = Array.from(customerMap.values())
                    .sort((a, b) => a.customerName.localeCompare(b.customerName));
                
                setCustomers(uniqueCustomers);
            } else {
                setCustomers([]);
            }
        } catch (error) {
            setCustomers([]);
        }
    };
    
    fetchCustomersFromUsers();
}, []);


    const handlebtnClick = () => {
        navigate('/users/add');
    }
    useEffect(() => {
        setCurrentPage(1);
    }, [allProfiles]);

    // Filtered lists for searchable dropdowns
    const filteredCustomersList = useMemo(() => {
        const list = customers || [];
        const normalize = (s: any) => String(s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
        const term = normalize(customerSearch);
        if (!term) return list;
        return list.filter(c => {
            const name = normalize(c.customerName);
            const soldTo = normalize(c.soldTo);
            if (name === term || soldTo === term) return true;
            return name.includes(term) || soldTo.includes(term);
        });
    }, [customers, customerSearch]);

    const userTypeOptions = ['Internal', 'External'];
    const filteredUserTypesList = useMemo(() => {
        if (!userTypeSearch.trim()) return userTypeOptions;
        const term = userTypeSearch.toLowerCase();
        return userTypeOptions.filter(type => type.toLowerCase().includes(term));
    }, [userTypeSearch]);
   //scroll
   useEffect(() => {
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        return () => {
          document.body.style.overflow = previousBodyOverflow;
          document.documentElement.style.overflow = previousHtmlOverflow;
        };
      }, []);
      
    // Close any open dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Element | null;
            if (!target) return;
            const inDropdown = target.closest('.searchable-select-containers');
            if (!inDropdown) {
                setIsCustomerOpen(false);
                setIsUserTypeOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const state = location.state as { userDeleted?: boolean; message?: string } | null;
        if (state?.userDeleted) {
            toast.success(state.message || 'User deleted successfully');
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate]);

    const requestSort = (key: SortableKeys) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig?.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortArrow = (columnKey: SortableKeys) => {
        if (!sortConfig || sortConfig.key !== columnKey) {
            return <img className="arrow-default" src={ArrowDefault} alt="sort" />;
        }
        return sortConfig.direction === 'asc' ? (
            <img className="arrow-up" src={ArrowUp} alt="sort asc" />
        ) : (
            <img className="arrow-down" src={ArrowDown} alt="sort desc" />
        );
    };

    const sortedAndFilteredProfiles = useMemo(() => {
        // First apply search filter
        const searchFiltered = searchText.trim() === ''
            ? allProfiles
            : allProfiles.filter(profile => {
                const searchLower = searchText.toLowerCase();
                return (
                    (profile.customer || '').toLowerCase().includes(searchLower) ||
                    (profile.name || '').toLowerCase().includes(searchLower) ||
                    (profile.type || '').toLowerCase().includes(searchLower) ||
                    (profile.role || '').toLowerCase().includes(searchLower) ||
                    (profile.email || '').toLowerCase().includes(searchLower) ||
                    (profile.status || '').toLowerCase().includes(searchLower)
                );
            });


        // Then apply sorting
        const sortableItems = [...searchFiltered];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                const valA = (a[sortConfig.key] ?? '').toString().toLowerCase();
                const valB = (b[sortConfig.key] ?? '').toString().toLowerCase();

                if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });

        }
        return sortableItems;
    }, [allProfiles, searchText, sortConfig]);

    const totalItems = sortedAndFilteredProfiles.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const paginatedProfiles = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return sortedAndFilteredProfiles.slice(startIndex, endIndex);
    }, [sortedAndFilteredProfiles, currentPage, itemsPerPage]);

    const handlePreviousPage = () => {
        setCurrentPage(prev => Math.max(1, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prev => {
            if (totalPages === 0) {
                return 1;
            }
            return Math.min(totalPages, prev + 1);
        });
    };

    useEffect(() => {
        if (totalPages === 0 && currentPage !== 1) {
            setCurrentPage(1);
        } else if (totalPages > 0 && currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);

    
const handleCustomerChange = async (newCustomerId: string) => {
    setSelectedCustomerId(newCustomerId);
    setCurrentPage(1);
    setSearchText(''); // Consider resetting search

    setIsLoadingProfile(true);
    try {
        // Get all users data
        const response = await getUsers("All");
        
        if (response && response.Data && Array.isArray(response.Data)) {
            // Apply BOTH filters: customer AND user type
            let filteredData = response.Data;
            
            // Apply customer filter if selected
            if (newCustomerId && newCustomerId.trim() !== '') {
                filteredData = filteredData.filter((user: any) => {
                    // Try multiple possible customer ID fields
                    const userCustomerId = user.customerId || user.CustomerId || user.customerID;
                    const matchesCustomer = userCustomerId === newCustomerId;
                    
                    // Also check customer name if needed
                    const customerName = user.customer || user.customerName || '';
                    const customerMatch = customers.find(c => c.customerId === newCustomerId);
                    
                    return matchesCustomer || 
                           (customerMatch && customerName.includes(customerMatch.customerName));
                });
            }
            
            // Apply user type filter if selected (not "All")
            if (selectedUserType !== 'All') {
                filteredData = filteredData.filter((user: any) => {
                    const userType = (user.type || '').toString().toLowerCase();
                    const targetType = selectedUserType.toLowerCase();
                    return userType === targetType;
                });
            }
            
            const mappedData: ProfileData[] = filteredData.map((usr: any) => ({
                id: usr.id || Math.random().toString(36).substr(2, 9),
                customer: usr.customer || 'N/A',
                name: usr.name || 'N/A',
                type: usr.type || 'N/A',
                role: usr.role || 'N/A',
                email: usr.email || 'N/A',
                status: usr.status || 'N/A',
            }));
            
            setAllProfiles(mappedData);
        } else {
            setAllProfiles([]);
        }
    } catch (error) {
        setAllProfiles([]);
    } finally {
        setIsLoadingProfile(false);
    }
};

const handleUserTypeChange = async (newUserType: string) => {
    setSelectedUserType(newUserType);
    setCurrentPage(1);
    
    setIsLoadingProfile(true);
    try {
        const response = await getUsers("All");
        
        if (response && response.Data && Array.isArray(response.Data)) {
            // Apply BOTH filters: customer AND user type
            let filteredData = response.Data;
            
            // Apply customer filter if selected
            if (selectedCustomerId && selectedCustomerId.trim() !== '') {
                filteredData = filteredData.filter((user: any) => {
                    const userCustomerId = user.customerId || user.CustomerId || user.customerID;
                    const matchesCustomer = userCustomerId === selectedCustomerId;
                    
                    const customerName = user.customer || user.customerName || '';
                    const customerMatch = customers.find(c => c.customerId === selectedCustomerId);
                    
                    return matchesCustomer || 
                           (customerMatch && customerName.includes(customerMatch.customerName));
                });
            }
            
            // Apply user type filter if not "All"
            if (newUserType !== 'All') {
                filteredData = filteredData.filter((user: any) => {
                    const userType = (user.type || '').toString().toLowerCase();
                    const targetType = newUserType.toLowerCase();
                    return userType === targetType;
                });
            }
            
            const mappedData: ProfileData[] = filteredData.map((usr: any) => ({
                id: usr.id || Math.random().toString(36).substr(2, 9),
                customer: usr.customer || 'N/A',
                name: usr.name || 'N/A',
                type: usr.type || 'N/A',
                role: usr.role || 'N/A',
                email: usr.email || 'N/A',
                status: usr.status || 'N/A',
            }));
            
            setAllProfiles(mappedData);
        } else {
            setAllProfiles([]);
        }
    } catch (error) {
        setAllProfiles([]);
    } finally {
        setIsLoadingProfile(false);
    }
};


    const handleDownload = async () => {
        try {
            const response = await userDownload("All");

            // Create blob with CSV MIME type and proper encoding
            const blob = new Blob([response.data], {
                type: 'text/csv;charset=utf-8;'
            });

            // Create filename
            const filename = 'users_data.csv';

            // Create download link
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
        }

    }



    function handleProfileClick(id: string): void {
        // Navigate to edit page with user ID in URL
        navigate(`/users/edit/${id}`);
    }

    return (
        <div className="plant-list-container">
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                style={{ zIndex: 10002 }}
            />
            <TabNavigation tabs={tabConfig} className="tabs-plants" />
            <div className="table-header">
                <div className="header-actions">
                    <div className="search-container-plant">
                        <img className='search-icon-lg-user' src={SearchIcon} alt="search" />
                        <input
                            type="text"
                            placeholder="Search"
                            value={searchText}
                            onChange={handleSearch}
                            className="search-input-user user-placeholder"
                        />
                        {/* Customer searchable dropdown */}
                        <div className="searchable-select-containers" >
                            <div className="searchable-select-input" onClick={() => { const next = !isCustomerOpen; setIsCustomerOpen(next); setIsUserTypeOpen(false); }}>
                                <input
                                    type="text"
                                    value={isCustomerOpen ? customerSearch : (selectedCustomerId ? (customers.find(c => c.customerId === selectedCustomerId)?.customerName || '') : '')}
                                    onChange={(e) => setCustomerSearch(e.target.value)}
                                    onFocus={() => { /* no-op */ }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                                            setIsCustomerOpen(true);
                                        }
                                    }}
                                    placeholder={isCustomerOpen ? 'Search customers...' : (selectedCustomerId ? (customers.find(c => c.customerId === selectedCustomerId)?.customerName || '') : 'All Customers')}
                                    className="user-cust searchable-input"
                                    readOnly={!isCustomerOpen}
                                    style={{ height: "36px", borderRadius: "4px" }}
                                />
                                <span className={`dropdown-arrow profile-arrow ${isCustomerOpen ? 'open' : ''}`}><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.16797 7.5L10.0013 13.3333L15.8346 7.5" stroke="#FCFCFD" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                           </svg></span>
                            </div>
                            {isCustomerOpen && (
                                <div className="profile-searchable-dropdown">
                                    <div
                                        className={`dropdown-option ${selectedCustomerId === '' ? 'selected' : ''}`}
                                        onClick={() => { handleCustomerChange(''); setIsCustomerOpen(false); setCustomerSearch(''); }}
                                    >
                                        All Customers
                                    </div>
                                    {filteredCustomersList.map((customer, index) => {
                                        const label = `${customer.customerName}${customer.soldTo ? ` (${customer.soldTo})` : ''}`;
                                        return (
                                            <div
                                                key={customer.id || `${customer.customerId}-${index}`}
                                                className={`dropdown-option ${customer.customerId === selectedCustomerId ? 'selected' : ''}`}
                                                onClick={() => { handleCustomerChange(customer.customerId); setIsCustomerOpen(false); setCustomerSearch(''); }}
                                            >
                                                {label}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        &nbsp;&nbsp;
                        {/* User Type searchable dropdown */}
                        <div className="searchable-select-containers" >
                            <div className="searchable-select-input" onClick={() => { const next = !isUserTypeOpen; setIsUserTypeOpen(next); setIsCustomerOpen(false); }}>
                                <input
                                    type="text"
                                    value={isUserTypeOpen ? userTypeSearch : (selectedUserType || '')}
                                    onChange={(e) => setUserTypeSearch(e.target.value)}
                                    onFocus={() => { /* no-op */ }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                                            setIsUserTypeOpen(true);
                                        }
                                    }}
                                    placeholder={isUserTypeOpen ? 'Search user types...' : (selectedUserType || 'User Type: All')}
                                    className="user-cust searchable-input"
                                    readOnly={!isUserTypeOpen}
                                    style={{ height: "36px", borderRadius: "4px" }}
                                />
                                <span className={`dropdown-arrow profile-arrow ${isUserTypeOpen ? 'open' : ''}`}><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.16797 7.5L10.0013 13.3333L15.8346 7.5" stroke="#FCFCFD" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                           </svg></span>
                            </div>
                            {isUserTypeOpen && (
                                <div className="usertype-dropdown">
                                    <div
                                        className={`dropdown-option ${selectedUserType === 'All' ? 'selected' : ''}`}
                                        onClick={() => { handleUserTypeChange('All'); setIsUserTypeOpen(false); setUserTypeSearch(''); }}
                                    >
                                        User Type: All
                                    </div>
                                    {filteredUserTypesList.map((type, index) => (
                                        <div
                                            key={`${type}-${index}`}
                                            className={`dropdown-option ${type === selectedUserType ? 'selected' : ''}`}
                                            onClick={() => { handleUserTypeChange(type); setIsUserTypeOpen(false); setUserTypeSearch(''); }}
                                        >
                                            {type}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                
                <button className="download-button-user" onClick={handleDownload}>
                    <img className="download-icon-lg-user" src={DownloadIcon} alt="download" />
                    Download
                </button>
                <button className="create-user-button" onClick={handlebtnClick} disabled={btnDisabled}>
                    +&nbsp;&nbsp; Add User
                </button></div>
                
            </div>
            <div className="table-wrapper-profile">
                <table className="global-table  profile-table">
                    <thead>
                        <tr>
                            <th className="col-customer" onClick={() => requestSort('customer')}>
                                <div className="sort-header">
                                     <p className='customer-profile'>Customer</p>

                                    {getSortArrow('customer')}
                                   
                                </div>
                            </th>
                            <th className="col-site" onClick={() => requestSort('name')}>
                                <div className="sort-header">
                                    <p className='customer-profile'>Name</p>
                                    {getSortArrow('name')}
                                    
                                </div>
                            </th>
                            <th className="col-plant-user" onClick={() => requestSort('type')}>
                                <div className="sort-header">
                                    <p className='customer-profile'>User Type</p>
                                    {getSortArrow('type')}
                                    
                                </div>
                            </th>
                            <th className="col-sales-district-user" onClick={() => requestSort('role')}>
                                <div className="sort-header">
                                    <p className='customer-profile'>Role</p>
                                    {getSortArrow('role')}
                                    
                                </div>
                            </th>
                            <th className="col-sold-tos" onClick={() => requestSort('email')}>
                                <div className="sort-header">
                                    <p className='customer-profile'>Email</p>
                                    {getSortArrow('email')}
                                    
                                </div>
                            </th>

                            <th className="col-status-user" style={{width:'82px'}} onClick={() => requestSort('status')}>
                                <div className="sort-header">
                                    <p className='customer-profile'>Status</p>
                                    {getSortArrow('status')}
                                    
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoadingProfile ? (
                            <tr>
                                <td colSpan={6} className="table-loading">
                                    <Loader />
                                </td>
                            </tr>
                        ) : paginatedProfiles.length > 0 ? (
                            paginatedProfiles.map((profile) => (
                                <tr key={profile.id}>
                                    <td className="text-wrap" title={profile.customer}>
                                        {profile.customer}
                                    </td>
                                    <td className="text-wrap">
                                        <button
                                            onClick={() => handleProfileClick(profile.id)}
                                            className="content-link"
                                            title={profile.name}
                                        >
                                            {profile.name}

                                        </button>
                                    </td>

                                    <td className="text-wrap-type" title={profile.type}>
                                        {profile.type}
                                    </td>
                                    <td className="text-wrap-role" title={profile.role}>
                                        {profile.role}
                                    </td>
                                    <td className="text-wrap" title={profile.email}>
                                        {profile.email}
                                    </td>
                                    <td
  className={`content-status ${
    profile.status.toLowerCase() === 'active' || profile.status.toLowerCase() === 'enabled' 
      ? 'status-active profile-active-user' 
      : 'profile-inactive-user'
  }`}
  title={profile.status}
>
  <span className='status-text'>{profile.status}</span>
</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={6} className="table-empty">No data found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
                
            </div>
            <div className="pagination-fixed-container">
                    <div className="users-pagination">
                        <button
                            className="users-pagination-prev"
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1 || totalItems === 0}
                        >
                         <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginBottom:"-5px"}}>
               <path d="M15.8333 10L4.16668 10M4.16668 10L10 15.8334M4.16668 10L10 4.16669" stroke="#FCFCFD" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
               </svg>  Previous
                        </button>
                        <span className="users-pagination-info">
                            Showing {totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to {totalItems === 0 ? 0 : Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} users
                        </span>
                        <button
                            className="users-pagination-next"
                            onClick={handleNextPage}
                            disabled={totalItems === 0 || currentPage >= totalPages}
                        >
                            Next  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginBottom:"-5px"}}>
             <path d="M4.16675 9.99996H15.8334M15.8334 9.99996L10.0001 4.16663M15.8334 9.99996L10.0001 15.8333" stroke="#FCFCFD" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
             </svg>
                        </button>
                    </div>
                </div>
        </div>
    );
};

export default Profile;